import { TypeQuestionAndAnswer } from "@contentful/types";
import { Block, Document, Inline, Text } from "@contentful/rich-text-types";

type RichTextNode = Document | Block | Inline | Text;

const isTextNode = (node: RichTextNode): node is Text =>
  node.nodeType === "text";

const INLINE_NODE_TYPES = new Set<string>([
  "hyperlink",
  "entry-hyperlink",
  "asset-hyperlink",
]);

function nodeToPlainText(node: RichTextNode): string {
  if (isTextNode(node)) {
    return node.value;
  }
  const text = node.content.map(nodeToPlainText).join("");
  return INLINE_NODE_TYPES.has(node.nodeType) ? text : `${text}\n`;
}

export function richTextToPlainText(document: Document): string {
  return nodeToPlainText(document).replace(/\n+/g, "\n").trim();
}

/**
 * Emits schema.org FAQPage structured data so search engines and AI crawlers
 * can read the full Q&A content regardless of the accordion's UI state.
 */
export function FaqJsonLd({
  questionsAndAnswers,
}: {
  questionsAndAnswers: TypeQuestionAndAnswer<
    "WITHOUT_UNRESOLVABLE_LINKS",
    string
  >[];
}) {
  const mainEntity = questionsAndAnswers.flatMap((questionAndAnswer) => {
    const { question, answer } = questionAndAnswer.fields;
    const answerText = answer ? richTextToPlainText(answer) : "";
    if (!question || !answerText) {
      return [];
    }
    return [
      {
        "@type": "Question",
        name: question,
        acceptedAnswer: {
          "@type": "Answer",
          text: answerText,
        },
      },
    ];
  });

  if (mainEntity.length === 0) {
    return null;
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        // "<" escaped so answer text can never break out of the script tag
        __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c"),
      }}
    />
  );
}
